import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { Account, AccountApi, AccountRequest } from '@venecia/apis/identity';
import { InputBlock } from '@venecia/molecules';
import { ConfirmDialogService, ErrorMessageService, MessageService, TraceService } from '@venecia/services';
import { UrlUtil } from '@venecia/utils';

@Component({
  selector: 'app-account-modal',
  imports: [CommonModule, InputBlock, ReactiveFormsModule, TranslateModule],
  templateUrl: './account-modal.html',
  styleUrl: './account-modal.scss',
})
export class AccountModal implements OnChanges {
  private readonly accountApi = inject(AccountApi);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly errorMessageService = inject(ErrorMessageService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly messageService = inject(MessageService);
  private readonly traceService = inject(TraceService);
  private readonly translateService = inject(TranslateService);

  @Input() account: Account | null = null;
  @Input() open = false;
  @Output() readonly closed = new EventEmitter<void>();
  @Output() readonly saved = new EventEmitter<void>();

  readonly detailMode = signal<'create' | 'edit'>('create');
  readonly saving = signal(false);
  readonly submitted = signal(false);

  readonly selectedCreatedAt = signal<string | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly selectedUpdatedAt = signal<string | null>(null);

  readonly isCreateMode = computed(() => this.detailMode() === 'create');
  readonly isEditMode = computed(() => this.detailMode() === 'edit');

  readonly form = this.formBuilder.nonNullable.group({
    logoUrl: ['', [Validators.maxLength(1024), UrlUtil.urlValidator()]],
    name: ['', [Validators.required, Validators.maxLength(256)]],
    webUrl: ['', [Validators.maxLength(1024), UrlUtil.urlValidator()]],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.open || (!changes['account'] && !changes['open'])) {
      return;
    }

    this.configureFromInputs();
  }

  closeDetailModal(force = false): void {
    if (!force && this.saving()) {
      return;
    }

    this.closed.emit();
  }

  async save(): Promise<void> {
    this.submitted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const confirmed = await this.confirmSave();
    if (!confirmed) {
      return;
    }

    this.saving.set(true);
    try {
      const payload = this.toApiModel();
      if (this.isCreateMode()) {
        await firstValueFrom(this.accountApi.create(payload));
        this.messageService.success(await this.translate('pages.accounts.messages.create-success'));
      } else {
        await firstValueFrom(this.accountApi.update(this.selectedIdOrThrow(), payload));
        this.messageService.success(await this.translate('pages.accounts.messages.update-success'));
      }

      this.saved.emit();
    } catch (error) {
      this.traceService.internal(error, true);
      this.messageService.error(await this.errorMessageService.resolveApiError(error, 'pages.accounts.save-error'));
    } finally {
      this.saving.set(false);
    }
  }

  hasError(controlName: keyof AccountFormModel, errorName: string): boolean {
    return this.form.controls[controlName].hasError(errorName);
  }

  isInvalid(controlName: keyof AccountFormModel): boolean {
    const control = this.form.controls[controlName];
    return (control.dirty || control.touched || this.submitted()) && control.invalid;
  }

  private configureFromInputs(): void {
    this.submitted.set(false);

    const account = this.account;
    if (!account) {
      this.detailMode.set('create');
      this.selectedCreatedAt.set(null);
      this.selectedId.set(null);
      this.selectedUpdatedAt.set(null);

      this.form.reset({
        logoUrl: '',
        name: '',
        webUrl: '',
      });
      return;
    }

    this.detailMode.set('edit');
    this.selectedCreatedAt.set(account['created-at'] ?? null);
    this.selectedId.set(account.id);
    this.selectedUpdatedAt.set(account['updated-at'] ?? null);

    this.form.reset({
      logoUrl: account['logo-url'] ?? '',
      name: account.name,
      webUrl: account['web-url'] ?? '',
    });
  }

  private selectedIdOrThrow(): number {
    const id = this.selectedId();
    if (id === null) {
      throw new Error('Account id is required for update');
    }
    return id;
  }

  private toApiModel(): AccountRequest {
    const formValue = this.form.getRawValue();

    return {
      id: this.selectedId(),
      'logo-url': formValue.logoUrl.trim() || undefined,
      name: formValue.name.trim(),
      'web-url': formValue.webUrl.trim() || undefined,
    };
  }

  private async translate(key: string): Promise<string> {
    return firstValueFrom(this.translateService.get(key));
  }

  private async translateMap(keys: readonly string[]): Promise<Record<string, string>> {
    return firstValueFrom(this.translateService.get([...keys]));
  }

  private async confirmSave(): Promise<boolean> {
    const keys = [
      'common.confirm-save.cancel',
      'common.confirm-save.confirm',
      'common.confirm-save.message',
      'common.confirm-save.title',
    ] as const;

    const i18n = await this.translateMap(keys);
    return this.confirmDialogService.open({
      cancelText: i18n['common.confirm-save.cancel'],
      confirmText: i18n['common.confirm-save.confirm'],
      message: i18n['common.confirm-save.message'],
      title: i18n['common.confirm-save.title'],
    });
  }
}

interface AccountFormModel {
  logoUrl: string;
  name: string;
  webUrl: string;
}
